import {
  eventChapterOf,
  findEventEntry,
  upcomingEvents,
} from "@repo/vihangam-yog-shared";
import { useMemo, useState } from "react";

import { CHAPTER } from "../../../chapter";
import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT, STEP_HEADING } from "../ui";
import { useSendFlow } from "../useSendFlow";

/** The backend features at most this many events in one email. */
const MAX_FEATURED = 4;

/**
 * After the event: "Thank you for coming" to the people who were checked
 * in, or "Sorry we missed you" to the people who booked and didn't come.
 * One email and one request for both; the sender can pick which upcoming
 * events it invites people to.
 */
export function FollowUpPanel({
  eventSlug,
  audience,
  audienceCount,
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  eventSlug: string;
  audience: "attended" | "absent";
  audienceCount: number;
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const missed = audience === "absent";
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const followUp = trpc.adminSendEventFollowUp.useMutation();

  // The events this email can invite people to: the next ones in this
  // event's own chapter, so a Christchurch guest isn't sent to Sydney.
  const choices = useMemo(() => {
    const entry = findEventEntry(eventSlug);
    return upcomingEvents({
      chapter: entry ? eventChapterOf(entry) : CHAPTER.id,
      excludeSlug: eventSlug,
      limit: 8,
    });
  }, [eventSlug]);
  const [featured, setFeatured] = useState<string[]>([]);

  const toggleFeatured = (slug: string) => {
    flow.optionsChanged();
    setFeatured((prev) =>
      prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : prev.length >= MAX_FEATURED
          ? prev
          : [...prev, slug],
    );
  };

  const send = (mode: "test" | "all") => {
    followUp.mutate(
      {
        eventSlug,
        mode,
        audience,
        featureEventSlugs: featured.length > 0 ? featured : undefined,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo}. ${
                    res.upcomingCount
                      ? `It invites them to ${res.upcomingCount} upcoming ${res.upcomingCount === 1 ? "event" : "events"}. `
                      : ""
                  }Open it, check it, then send to ${missed ? "everyone who didn't come" : "everyone who came"}.`,
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                missed
                  ? "Everyone who booked came, so there's no one to send this to."
                  : "No one has been checked in, so there's no one to send this to.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const busy = followUp.isPending;
  const next = choices[0];

  return (
    <>
      <p className={BODY_TEXT}>
        {missed
          ? "A kind note to the people who booked but didn't come, with photos and an invitation to the next event."
          : "A thank-you to the people who came, with links to our next events and social media. Best sent after the event."}
      </p>

      {choices.length > 0 ? (
        <fieldset className="mt-5 border-t border-[color:var(--color-line)] pt-5">
          <legend className={STEP_HEADING}>
            Which events should the email invite them to?
          </legend>
          <p className="mt-1.5 text-[15px] leading-relaxed text-[color:var(--color-ink-soft)]">
            {featured.length === 0
              ? `If you don't pick any, it invites them to the next event${
                  next
                    ? `: ${next.dateLabel}${next.venue?.suburb ? `, ${next.venue.suburb}` : ""}`
                    : ""
                }. Pick up to ${MAX_FEATURED} if that isn't right.`
              : `${featured.length} picked (up to ${MAX_FEATURED}). The first one you pick is shown biggest.`}
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {choices.map((e) => {
              const picked = featured.includes(e.slug);
              const order = featured.indexOf(e.slug) + 1;
              const full = !picked && featured.length >= MAX_FEATURED;
              return (
                <li key={e.slug}>
                  <button
                    type="button"
                    onClick={() => toggleFeatured(e.slug)}
                    disabled={busy || full}
                    aria-pressed={picked}
                    className={`flex min-h-12 w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                      picked
                        ? "border-[color:var(--color-amber)] bg-[color:var(--color-amber)]/10"
                        : "border-[color:var(--color-line)] hover:border-[color:var(--color-line-strong)]"
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[13px] font-semibold tabular-nums ${
                        picked
                          ? "border-[color:var(--color-amber)] bg-[color:var(--color-amber)] text-[color:var(--color-bg)]"
                          : "border-[color:var(--color-line-strong)]"
                      }`}
                    >
                      {picked ? order : ""}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] text-[color:var(--color-ink)]">
                        {e.title}
                      </span>
                      <span className="block text-[14px] text-[color:var(--color-ink-soft)]">
                        {e.dateLabel}
                        {e.venue?.suburb ? ` · ${e.venue.suburb}` : ""}
                      </span>
                    </span>
                    {picked ? (
                      <span className="shrink-0 text-[14px] font-semibold text-[color:var(--color-amber)]">
                        Picked
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ) : null}

      <TestThenSend
        flow={flow}
        emailName={missed ? "Sorry we missed you" : "Thank you for coming"}
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText={
          missed
            ? "Everyone who booked came, so there's no one to send this to."
            : "No one has been checked in yet. Check people in at Reception first."
        }
        busy={busy}
        onSendTest={() => send("test")}
        onSendAll={() => send("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
